/**
 * ConversationItem — Single conversation row in sidebar.
 *
 * Layer: C (UX/CLI)
 * Spec: Uppdrag C §2.2
 *
 * @decision Model badge colors: purple=Opus, blue=Sonnet, green=Haiku (spec §2.2).
 * @decision Relative time helper inlined — no date-fns dependency.
 * @decision Delete button visible on hover only (desktop), always on mobile via long-press later.
 */

import { useState, type MouseEvent } from 'react';

// ─── Types ────────────────────────────────────────────────────────────

export interface ConversationItemProps {
  id: string;
  title: string | null;
  model: string;
  updatedAt: string;
  isActive: boolean;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
}

// ─── Model badge config ───────────────────────────────────────────────

interface ModelBadge {
  label: string;
  dotClass: string;
}

function getModelBadge(model: string): ModelBadge {
  const m = model.toLowerCase();
  if (m.includes('opus')) return { label: 'Opus', dotClass: 'bg-purple-400' };
  if (m.includes('haiku')) return { label: 'Haiku', dotClass: 'bg-emerald-400' };
  // Default: Sonnet (most common)
  return { label: 'Sonnet', dotClass: 'bg-blue-400' };
}

// ─── Relative time ────────────────────────────────────────────────────

function relativeTime(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diffMs = now - then;

  if (Number.isNaN(diffMs) || diffMs < 0) return 'Just nu';

  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'Just nu';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min sedan`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} tim sedan`;

  // Show date for older items
  const date = new Date(isoString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === yesterday.toDateString()) {
    return `Igår ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  }

  return `${date.getDate()}/${date.getMonth() + 1} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
}

// ─── Component ────────────────────────────────────────────────────────

export function ConversationItem({
  id,
  title,
  model,
  updatedAt,
  isActive,
  onSelect,
  onDelete,
}: ConversationItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const badge = getModelBadge(model);

  const handleClick = () => {
    onSelect(id);
  };

  const handleDelete = (e: MouseEvent) => {
    e.stopPropagation();
    onDelete(id);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={[
        'group w-full flex items-start gap-3 px-3 py-2.5 rounded-lg text-left transition-colors duration-150',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50',
        isActive
          ? 'bg-accent/10 border-l-2 border-accent pl-[10px]'
          : 'hover:bg-bg-tertiary/50 border-l-2 border-transparent pl-[10px]',
      ].join(' ')}
      aria-current={isActive ? 'true' : undefined}
    >
      {/* Model dot */}
      <span
        className={`mt-1.5 flex-shrink-0 w-2 h-2 rounded-full ${badge.dotClass}`}
        title={badge.label}
        aria-label={`Modell: ${badge.label}`}
      />

      {/* Title + meta */}
      <span className="flex-1 min-w-0">
        <span className="block truncate text-sm font-medium text-text-primary">
          {title || 'Ny konversation'}
        </span>
        <span className="block text-xs text-text-secondary/50 mt-0.5">
          {relativeTime(updatedAt)}
        </span>
      </span>

      {/* Delete button (hover only) */}
      {isHovered && (
        <button
          type="button"
          onClick={handleDelete}
          className={[
            'flex-shrink-0 mt-0.5 p-1 rounded transition-colors',
            'text-text-secondary/50 hover:text-error hover:bg-error/10',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/50',
          ].join(' ')}
          title="Radera konversation"
          aria-label={`Radera: ${title || 'Ny konversation'}`}
        >
          {/* Trash icon (inline SVG — no dependency) */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 6h18" />
            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
            <line x1="10" y1="11" x2="10" y2="17" />
            <line x1="14" y1="11" x2="14" y2="17" />
          </svg>
        </button>
      )}
    </button>
  );
}

// ─── Exports for grouping logic ───────────────────────────────────────

export { relativeTime, getModelBadge };
